import { useCallback, useEffect, useState } from 'react'
import { adminMutate, apiRead, useToast } from '../admin/primitives.js'
import { EVT_ASSOCIATIONS, signalerAssociations, type ResultatGeste } from './associations-model.js'

/**
 * EXPLOITATION › ASSOCIATIONS — les lectures et les gestes.
 *
 * Chaque geste est IMMÉDIAT et s'annule d'un clic : le message qui suit porte
 * « ANNULER » (la convention des messages de Gestion), qui rejoue
 * `/gestes/:id/annuler` — le serveur rétablit exactement l'état d'avant, et
 * seulement ce que personne n'a retouché depuis. Après chaque geste, tous les
 * écrans ouverts se relisent (`EVT_ASSOCIATIONS`) : la liste, la fiche, le
 * badge de la barre latérale et la tuile du hub ne se contredisent jamais.
 */

export type Lecture<T> =
  | { phase: 'chargement' }
  | { phase: 'ok'; data: T }
  | { phase: 'erreur'; status: number; message: string }

/**
 * Lit une route et la relit à chaque geste (d'ici ou d'ailleurs). Une erreur
 * se DIT, avec le statut : 403 (pas le droit), 404 (fiche disparue) et une
 * panne ne se confondent pas. Une relecture garde l'écran affiché.
 */
export function useLecture<T>(chemin: string | null, relireMs = 0): { etat: Lecture<T>; relire: () => void } {
  const [etat, setEtat] = useState<Lecture<T>>({ phase: 'chargement' })
  const [tour, setTour] = useState(0)
  const relire = useCallback(() => setTour((n) => n + 1), [])
  useEffect(() => {
    if (!chemin) return
    let vivant = true
    void apiRead<T & { message?: string }>(chemin)
      .then((r) => {
        if (!vivant) return
        if (r.status === 200) setEtat({ phase: 'ok', data: r.data })
        else setEtat({ phase: 'erreur', status: r.status, message: r.data.message ?? messageStatut(r.status) })
      })
      .catch(() => { if (vivant) setEtat({ phase: 'erreur', status: 0, message: 'Gestion ne répond pas : vérifiez la connexion, puis réessayez.' }) })
    return () => { vivant = false }
  }, [chemin, tour])
  useEffect(() => {
    window.addEventListener(EVT_ASSOCIATIONS, relire)
    const t = relireMs > 0 ? window.setInterval(relire, relireMs) : null
    return () => { window.removeEventListener(EVT_ASSOCIATIONS, relire); if (t) window.clearInterval(t) }
  }, [relire, relireMs])
  return { etat, relire }
}

export function messageStatut(status: number): string {
  if (status === 403) return 'Vous n’avez pas le droit de consulter les associations.'
  if (status === 404) return 'Cette association n’existe pas (ou plus).'
  return 'La lecture a échoué. Réessayez dans un instant.'
}

/**
 * Les gestes. `envoyer` rend `null` quand c'est fait (le message est déjà
 * dit, avec « ANNULER »), sinon la phrase d'erreur du serveur — à afficher
 * à côté du formulaire qui l'a provoquée, jamais perdue dans un toast.
 */
export function useGestes() {
  const toast = useToast()
  const annuler = useCallback(async (geste: string) => {
    const r = await adminMutate<ResultatGeste & { error?: string }>('POST', `/api/v1/associations/gestes/${geste}/annuler`, {})
    if (r.status !== 200 || !('geste' in r.data)) { toast('error', r.data.message ?? 'Annulation impossible.'); return }
    toast('ok', r.data.message || 'Geste annulé.')
    signalerAssociations()
  }, [toast])
  const envoyer = useCallback(async (methode: 'POST' | 'PUT', chemin: string, corps: unknown, o: { annulable?: boolean } = {}): Promise<string | null> => {
    const r = await adminMutate<ResultatGeste & { error?: string }>(methode, chemin, corps)
    if (r.status !== 200 || !r.data.ok) return r.data.message ?? 'Geste impossible.'
    const geste = 'geste' in r.data ? r.data.geste : null
    toast('ok', r.data.message ?? 'Fait.', geste && o.annulable !== false
      ? { action: { label: 'ANNULER', onAction: () => { void annuler(geste) } }, durationMs: 9000 }
      : undefined)
    signalerAssociations()
    return null
  }, [toast, annuler])
  return { envoyer, annuler }
}
